import { useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createReminder } from './api';
import type { Message } from './types';

// Extrae texto plano del cuerpo (HTML) de una nota para prerellenar el título.
function plainExcerpt(html: string | null | undefined, max = 100): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function tomorrowISO(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function noteDate(input: string | null | undefined): string {
  if (!input) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${String(d.getFullYear()).slice(-2)}`;
}

// Modal compacto para crear un recordatorio privado a partir de una nota de un
// hilo. Estampa el origen (task_id + message.id) para el enlace de vuelta.
export default function ReminderFromNote({
  message, userId, onClose, onCreated,
}: {
  message: Message;
  userId: string;
  onClose: () => void;
  onCreated?: () => void;
}) {
  const navigate = useNavigate();
  const [titulo, setTitulo] = useState(() => plainExcerpt(message.body) || 'Recordatorio');
  const [fecha, setFecha] = useState(tomorrowISO);
  const [nota, setNota] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const autor = message.author?.nombre || 'alguien';

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (!titulo.trim() || !fecha) { setError('Pon un título y una fecha.'); return; }
    setBusy(true);
    try {
      await createReminder({
        titulo,
        fecha_vencimiento: fecha,
        nota,
        source_task_id: message.task_id,
        source_message_id: message.id,
      }, userId);
      onCreated?.();
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el recordatorio');
      setBusy(false);
    }
  };

  return (
    <div style={styles.overlay} onMouseDown={onClose}>
      <form style={styles.modal} onSubmit={submit} onMouseDown={(e) => e.stopPropagation()}>
        <h2 style={styles.title}>Recordarme esta nota</h2>
        <div style={styles.context}>
          <span style={styles.contextDim}>Desde la nota de</span>
          <strong style={{ color: message.author?.color || '#475569' }}>{autor}</strong>
          <span style={styles.contextDim}>· {noteDate(message.created_at)}</span>
        </div>

        {done ? (
          <>
            <p style={styles.okMsg}>✓ Recordatorio creado para el {noteDate(`${fecha}T00:00:00`)}. Lo verás en tu Agenda.</p>
            <div style={styles.actions}>
              <button type="button" onClick={onClose} style={styles.cancelBtn}>Cerrar</button>
              <button type="button" onClick={() => navigate('/agenda')} style={styles.saveBtn}>Ir a la Agenda</button>
            </div>
          </>
        ) : (
          <>
            <label style={styles.label}>Título</label>
            <input autoFocus value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={200} style={styles.input} />

            <label style={styles.label}>Fecha</label>
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={styles.input} />

            <label style={styles.label}>Nota (opcional)</label>
            <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2} placeholder="Un detalle…" style={styles.textarea} />

            <p style={styles.hint}>Privado: solo tú lo verás. Tendrás un enlace de vuelta a esta nota.</p>
            {error && <p style={styles.error}>{error}</p>}

            <div style={styles.actions}>
              <button type="button" onClick={onClose} style={styles.cancelBtn}>Cancelar</button>
              <button type="submit" disabled={busy} style={styles.saveBtn}>{busy ? '…' : 'Crear recordatorio'}</button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400, padding: 20 },
  modal: { background: '#fff', borderRadius: 16, padding: 26, width: 420, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 7, boxShadow: '0 24px 60px rgba(15,23,42,0.25)' },
  title: { fontSize: 19, fontWeight: 800, color: '#0f172a', margin: 0 },
  context: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5, fontSize: 13, marginBottom: 6 },
  contextDim: { color: '#94a3b8' },
  label: { fontSize: 12, fontWeight: 700, color: '#475569', marginTop: 6 },
  input: { border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#fff' },
  textarea: { border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', background: '#fff', resize: 'vertical', fontFamily: 'inherit' },
  hint: { fontSize: 12, color: '#94a3b8', margin: '6px 0 0', lineHeight: 1.45 },
  okMsg: { fontSize: 14, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px', margin: '6px 0 0', lineHeight: 1.5 },
  error: { color: '#dc2626', fontSize: 13, margin: '4px 0 0' },
  actions: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 16 },
  cancelBtn: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, color: '#475569', cursor: 'pointer' },
  saveBtn: { background: '#16a34a', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer' },
};
